import { CheckIcon } from '@chakra-ui/icons'
import { CircularProgress, Flex, HStack, Text } from '@chakra-ui/react'
import { colors, Descendant, Template } from '@hb/shared'
import isEqual from 'lodash.isequal'

import { sleep } from '@hb/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import saveIcon from '../../icons/save_old_green.svg'
import { SvgIcon } from '../../icons/SvgIcon'
import { TextEditorToolbar } from '../../types/editor'
import { ActionButton } from '../Buttons/ActionButton'
import { TextInputElement } from '../forms/Input/Text/styles'
import { TextEditorTemplateToolbar } from '../RichText/TemplateToolbar'
import { TextEditor } from '../RichText/TextEditor'
import { TextEditorProps } from '../RichText/types'
import { useTemplateEditor } from './contexts'
import { useTemplateData } from './contexts/data'
import { EditorCloseButton } from './EditorCloseButton'
import { getWithVariables } from './withVariables'

const convertValueToV2 = (value: Descendant[]): Descendant[] =>
  value.map(node => {
    if (node.type === 'variable') {
      return {
        type: 'span',
        children: [{ text: '' }, node, { text: '' }],
      }
    }
    return node
  })

export const TemplateEditor = ({
  width,
  height,
  baseZoom,
  modalWidth,
  onClose,
}: {
  width: TextEditorProps['width']
  height: TextEditorProps['height']
  modalWidth: TextEditorProps['modalWidth']
  baseZoom: TextEditorProps['baseZoom']
  onClose: () => void
}) => {
  const { onSubmit, template, isLoading: templateLoading } = useTemplateEditor()
  const { type: templateType } = useTemplateData()
  const [templateName, setTemplateName] = useState(template?.name ?? '')
  useEffect(() => setTemplateName(template?.name ?? ''), [template])
  const [isLoading, setIsLoading] = useState(false)
  const [remountingForConvert, setRemountingForConvert] = useState(false)
  const [convertingToV2, setConvertingToV2] = useState(false)
  const [editedValue, setValue] = useState<Descendant[]>(
    template?.templateText && Array.isArray(template?.templateText) ? template?.templateText : [],
  )
  const dirty = useMemo(
    () => !isEqual(template?.templateText, editedValue),
    [template, editedValue],
  )

  // if value, assume it's a v1 template if not specified
  // if no value, start with v2
  const usedVersion = useMemo(() => {
    if (convertingToV2) return 'v2'
    return template?.templateText ? template.editorVersion || 'v1' : 'v2'
  }, [template, convertingToV2])

  const convertToV2 = useCallback(async () => {
    setRemountingForConvert(true)
    await sleep(20)
    setValue(convertValueToV2(editedValue))
    setConvertingToV2(true)
    await sleep(200)
    setRemountingForConvert(false)
  }, [editedValue])

  const [initializing, setInitializing] = useState(true)
  useEffect(() => {
    if (initializing && !templateLoading) {
      setValue(
        template?.templateText && Array.isArray(template.templateText) ? template.templateText : [],
      )
      setInitializing(false)
    }
  }, [initializing, template, templateLoading])

  const handleSubmit = useCallback(async () => {
    setIsLoading(true)
    const submitted: Omit<Template, 'rank'> & { id?: string } = {
      name: templateName,
      templateText: editedValue,
      editorVersion: usedVersion,
    }

    if (template) submitted.id = template.id

    const res = await onSubmit(submitted)
    setIsLoading(false)
    return res
  }, [templateName, onSubmit, editedValue, usedVersion, template])

  const canUpgradeVersion = useMemo(
    () => template?.templateText && (!usedVersion || usedVersion === 'v1'),
    [usedVersion, template],
  )

  const decorators = useMemo(() => [getWithVariables(usedVersion)], [usedVersion])
  const toolbars = useMemo<TextEditorToolbar[]>(() => [TextEditorTemplateToolbar], [])
  if (remountingForConvert || initializing) {
    return (
      <HStack p={4} pr={16} border={6}>
        <CircularProgress color={colors.green.hex} isIndeterminate size={5} />
        <Text fontFamily="Open Sans">
          {remountingForConvert ? 'Converting to v2...' : 'Template loading...'}{' '}
        </Text>
      </HStack>
    )
  }
  return (
    <TextEditor
      version={usedVersion}
      value={editedValue && Array.isArray(editedValue) ? editedValue : []}
      onChange={v => {
        setValue(v)
      }}
      width={width}
      modalWidth={modalWidth}
      height={height}
      baseZoom={baseZoom}
      toolbars={toolbars}
      decorators={decorators}>
      <Flex px={3} gap={2} width="100%" minH={12} align="center">
        <Flex align="center" flex={1}>
          <TextInputElement
            placeholder={templateType === 'consentForm' ? 'Consent Form Name' : 'Template Name'}
            value={templateName}
            style={{
              fontFamily: 'Hero-New',
              fontSize: '1rem',
              border: '1px solid #cdcdcd',
              background: 'white',
              borderRadius: '5px',
              padding: '0.2rem 0.4rem',
            }}
            onChange={({ target: { value: v } }) => {
              setTemplateName(v)
            }}
          />
        </Flex>
        {canUpgradeVersion && (
          <ActionButton isLoading={remountingForConvert} onClick={convertToV2} bg="white" size="xs">
            Convert to v2
          </ActionButton>
        )}
        <ActionButton gap={1} bg="gray.50" isLoading={isLoading} onClick={handleSubmit} size="xs">
          {dirty ? (
            <>
              <SvgIcon width="16px" src={saveIcon} />
              <Text>Save Template</Text>
            </>
          ) : (
            <>
              <CheckIcon />
              <Text>Saved</Text>
            </>
          )}
        </ActionButton>
        <EditorCloseButton onClose={onClose} />
      </Flex>
    </TextEditor>
  )
}
